import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  MdPendingActions,
  MdCheckCircle,
  MdCancel,
  MdPerson,
  MdOpenInNew,
  MdRefresh,
} from 'react-icons/md';
import api from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import './PendingDoneRequests.css';

const statusLabel = (s) => {
  const m = {
    todo: 'To Do',
    'in-progress': 'In Progress',
    review: 'Review',
    done: 'Done',
  };
  return m[s] || s;
};

const PendingDoneRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/done-requests/pending');
      setRequests(data.data || []);
      window.dispatchEvent(new Event('pending-done-count-changed'));
    } catch {
      toast.error('Failed to load pending completion requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async (requestId) => {
    setBusyId(requestId);
    try {
      await api.post(`/done-requests/${requestId}/approve`);
      toast.success('Task marked as done');
      await fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not approve request');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (requestId) => {
    setBusyId(requestId);
    try {
      const { data } = await api.post(`/done-requests/${requestId}/reject`);
      toast.success(data.message || 'Request declined; assignee notified by email');
      await fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not decline request');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="pending-done-page">
      <div className="pending-done-header">
        <div>
          <h1>
            <MdPendingActions size={28} />
            Pending Done Requests
          </h1>
          <p className="pending-done-subtitle">
            Staff have requested to mark these tasks complete. Review each task and approve or
            decline.
          </p>
        </div>
        <button
          type="button"
          className="pending-done-refresh"
          onClick={fetchRequests}
          disabled={loading}
        >
          <MdRefresh size={18} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="pending-done-loading">
          <div className="spinner" />
        </div>
      ) : requests.length === 0 ? (
        <div className="pending-done-empty">
          <MdCheckCircle size={48} />
          <p>No pending completion requests</p>
          <span>When assigned staff request to mark a task as done, they will appear here.</span>
        </div>
      ) : (
        <div className="pending-done-list">
          {requests.map((req) => {
            const task = req.task;
            if (!task) return null;
            const assignee = req.requestedBy;
            const taskId = task._id;
            const busy = busyId === req._id;

            return (
              <article key={req._id} className="pending-done-card">
                <div className="pending-done-card-top">
                  <div className="pending-done-card-title-wrap">
                    <h2>{task.title}</h2>
                    <span className={`pending-done-status-chip status-${task.status || 'todo'}`}>
                      Current: {statusLabel(task.status)}
                    </span>
                  </div>
                  <Link
                    to={`/tasks/${taskId}`}
                    className="pending-done-open-link"
                  >
                    <MdOpenInNew size={16} />
                    View task
                  </Link>
                </div>

                {task.description && (
                  <p className="pending-done-desc">
                    {task.description.length > 280
                      ? `${task.description.slice(0, 280)}…`
                      : task.description}
                  </p>
                )}

                <div className="pending-done-meta">
                  <span>
                    <MdPerson size={16} />
                    Requested by <strong>{assignee?.name || 'Staff'}</strong>
                  </span>
                  <span>
                    {format(new Date(req.createdAt), "MMM d, yyyy 'at' HH:mm")}
                  </span>
                  {task.priority && (
                    <span className="pending-done-priority">
                      Priority: {task.priority}
                    </span>
                  )}
                  {task.deadline && (
                    <span>
                      Deadline: {format(new Date(task.deadline), 'MMM d, yyyy')}
                    </span>
                  )}
                </div>

                <div className="pending-done-actions">
                  <button
                    type="button"
                    className="pending-done-btn pending-done-btn--approve"
                    disabled={busy}
                    onClick={() => handleApprove(req._id)}
                  >
                    <MdCheckCircle size={18} />
                    Mark task done
                  </button>
                  <button
                    type="button"
                    className="pending-done-btn pending-done-btn--reject"
                    disabled={busy}
                    onClick={() => handleReject(req._id)}
                  >
                    <MdCancel size={18} />
                    Not done yet
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PendingDoneRequests;
